import React from "react";
import { Autocomplete, TextField, Box, Avatar, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { SteamAccount } from "../../store/slices/authSlice";
import { RootState } from "../../store";

interface SteamAccountSelectProps {
  value: SteamAccount | null;
  onChange: (steamAccount: SteamAccount | null) => void;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  placeholder?: boolean;
  showAvatar?: boolean;
}

const SteamAccountUserSelect: React.FC<SteamAccountSelectProps> = ({
                                                                     value,
                                                                     onChange,
                                                                     size = 'medium',
                                                                     variant = 'outlined',
                                                                     placeholder = true,
                                                                     showAvatar = false
                                                                   }) => {
  const authAccounts = useSelector((state: RootState) => state.auth.steamAccounts);

  return (
      <Autocomplete
          options={authAccounts}
          getOptionLabel={(option) => option.profileName || ''}
          value={value ?? undefined}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          disableClearable // removes x button
          openOnFocus
          onChange={(_, newValue) => {
            onChange(newValue);
          }}
          size={size}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
            },
            paper: {
              className: "shadow-2xl border border-neutral-700 bg-neutral-800 !overflow-visible !rounded-lg",
              elevation: 0,
              sx: {
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 20,
                  width: 12,
                  height: 12,
                  background: 'linear-gradient(135deg, #262626 50%, transparent 50%)',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 50,
                  borderLeft: '1px solid #404040',
                  borderTop: '1px solid #404040'
                },
                '&:has(li:first-of-type:hover) ::before, &:has(li:first-of-type[aria-selected="true"]) ::before': {
                  background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.15) 50%, transparent 50%)',
                }
              }
            },
            listbox: {
              className: "!p-0 relative z-10 !rounded-lg bg-transparent overflow-hidden",
              sx: {
                '& .MuiAutocomplete-option': {
                  padding: '0 !important',
                  margin: '0 !important',
                  backgroundColor: 'transparent !important',
                },
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: 'transparent !important',
                },
              }
            }
          }}
          renderOption={(props, option, state) => {
            const { key, ...optionProps } = props as any;
            const isSelected = value?.id === option.id;
            const isFirst = state.index === 0;

            return (
                <li key={option.id} {...optionProps}>
                  <Box
                      className={`flex items-center gap-3 px-1.5 py-1.5 transition-all cursor-pointer w-full ${
                          isSelected ? 'bg-indigo-500/15' : 'hover:bg-neutral-700/80'
                      }`}
                      sx={{
                        borderLeft: isSelected ? '3px solid #3f51b5' : '3px solid transparent',
                        borderBottom: '1px solid rgba(64, 64, 64, 0.3)',
                        borderTopLeftRadius: isFirst ? '8px' : '0px',
                        borderTopRightRadius: isFirst ? '8px' : '0px',
                      }}
                  >
                    <Avatar
                        src={option.avatarUrl || ''}
                        className="!h-9 !w-9 border border-neutral-700/50"
                    />

                    <Box className="flex flex-col items-start gap-0.5 min-w-0 flex-1">
                      <Box
                          className={`flex items-center justify-center text-[9px] px-1.5 h-[16px] rounded uppercase font-black tracking-tighter border shrink-0 ${
                              option.tradeable
                                  ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                  : 'bg-red-500/10 text-red-400 border-red-500/20'
                          }`}
                          sx={{ lineHeight: 1 }}
                      >
                        {option.tradeable ? 'Tradeable' : 'Locked'}
                      </Box>

                      <Typography
                          className={`text-[13px] leading-none truncate w-full ${
                              isSelected ? '!text-white !text-sm !font-semibold' : '!text-neutral-200 !text-sm !font-semibold'
                          }`}
                      >
                        {option.profileName}
                      </Typography>
                    </Box>
                  </Box>
                </li>
            );
          }}
          renderInput={(params) => (
              <TextField
                  {...params}
                  label={placeholder ? "Steam Account auswählen" : undefined}
                  variant={variant}
                  className="bg-neutral-700 rounded-lg select-none"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      cursor: 'pointer',
                      '& fieldset': { borderColor: 'transparent' },
                      '&:hover fieldset': { borderColor: 'transparent' },
                      '&.Mui-focused fieldset': {
                        borderColor: 'transparent',
                        borderWidth: '0px'
                      },
                      '& input': {
                        color: '#e5e5e5',
                        padding: '4px 2px !important',
                        cursor: 'pointer',
                        caretColor: 'transparent',
                        userSelect: 'none',
                        pointerEvents: 'none',
                        '&::selection': {
                          backgroundColor: 'transparent'
                        }
                      }
                    },
                    '& .MuiInputLabel-root': { color: '#737373', fontSize: '13px', userSelect: 'none' },
                    '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled': { color: '#a3a3a3' }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (showAvatar && value?.avatarUrl) ? (
                        <Avatar
                            src={value.avatarUrl}
                            variant="circular"
                            className="h-6 w-6 mr-1 ml-1 border border-neutral-600"
                        />
                    ) : params.InputProps.startAdornment,
                  }}
              />
          )}
      />
  );
};

export default SteamAccountUserSelect;